<template>
  <aside
    :class="[
      'bg-bg-1 border-r border-line flex flex-col transition-[width] duration-200',
      collapsed ? 'w-14' : 'w-[220px]',
    ]"
  >
    <!-- brand -->
    <div class="flex items-center gap-2.5 px-4 py-4 border-b border-line shrink-0">
      <div class="w-8 h-8 rounded-[5px] bg-lime text-black flex items-center justify-center shrink-0">
        <Globe :size="18" :stroke-width="2.25" />
      </div>
      <div v-if="!collapsed" class="min-w-0">
        <div class="text-[13px] font-semibold text-text leading-tight">Neutral Fuels</div>
        <div class="text-[11px] text-text-3 leading-tight">Admin</div>
      </div>
    </div>

    <!-- nav -->
    <nav class="flex-1 overflow-y-auto py-2">
      <template v-for="(group, gi) in items" :key="gi">
        <div
          v-if="group.category && !collapsed"
          class="px-4 pt-3 pb-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-text-4"
        >
          {{ group.category }}
        </div>
        <router-link
          v-for="item in group.links"
          :key="item.to"
          :to="item.to"
          v-slot="{ isActive, href, navigate }"
          custom
        >
          <a
            :href="href"
            @click="navigate"
            :class="[
              'flex items-center gap-2.5 px-4 py-[7px] text-[13px] border-l-2 cursor-pointer',
              'transition-colors',
              isActive
                ? 'bg-bg-2 text-text border-lime font-medium'
                : 'text-text-2 border-transparent hover:bg-bg-2 hover:text-text',
              collapsed ? 'justify-center px-0' : '',
            ]"
            :title="collapsed ? item.label : ''"
          >
            <component
              :is="item.icon"
              :size="16"
              :stroke-width="1.75"
              :class="isActive ? 'text-lime' : 'text-text-3'"
            />
            <span v-if="!collapsed" class="flex-1 truncate">{{ item.label }}</span>
            <span
              v-if="!collapsed && item.count != null"
              class="font-mono text-[10.5px] text-text-3 px-1.5 py-0.5 bg-bg border border-line-2 rounded"
            >
              {{ item.count }}
            </span>
          </a>
        </router-link>
      </template>
    </nav>

    <!-- footer: profile row (links to /my-account) + log out + collapse toggle -->
    <div class="border-t border-line shrink-0">
      <router-link
        to="/my-account"
        v-slot="{ isActive, href, navigate }"
        custom
      >
        <a
          :href="href"
          @click="navigate"
          :class="[
            'flex items-center gap-2.5 px-4 py-3 cursor-pointer transition-colors hover:bg-bg-2',
            collapsed ? 'justify-center px-0' : '',
          ]"
          :title="collapsed ? (displayName || 'Profile') : ''"
        >
          <div
            :class="[
              'w-8 h-8 rounded-[5px] bg-bg border text-text-2 font-mono text-[11px] font-semibold flex items-center justify-center shrink-0',
              isActive ? 'border-lime text-lime' : 'border-line-2',
            ]"
          >
            {{ userInitials }}
          </div>
          <div v-if="!collapsed" class="min-w-0 flex-1 leading-tight">
            <div class="text-[12.5px] text-text truncate">{{ displayName || 'My account' }}</div>
            <div class="font-mono text-[9.5px] uppercase tracking-[0.1em] text-lime-dim">
              {{ user?.role || '—' }}
            </div>
          </div>
        </a>
      </router-link>

      <button
        v-if="!collapsed"
        type="button"
        @click="$emit('logout')"
        class="w-full flex items-center gap-2.5 px-4 py-2 border-t border-line text-[12.5px] text-text-2 hover:bg-bg-2 hover:text-err transition-colors"
        title="Log out"
      >
        <LogOut :size="15" />
        <span>Log out</span>
      </button>
      <button
        v-else
        type="button"
        @click="$emit('logout')"
        class="w-full flex items-center justify-center py-2 border-t border-line text-text-2 hover:bg-bg-2 hover:text-err transition-colors"
        title="Log out"
        aria-label="Log out"
      >
        <LogOut :size="15" />
      </button>

      <button
        type="button"
        @click="$emit('toggle')"
        :class="[
          'w-full flex items-center gap-2 px-4 py-2 border-t border-line',
          'text-text-3 hover:bg-bg-2 hover:text-text text-[12px]',
          collapsed ? 'justify-center px-0' : '',
        ]"
        :title="collapsed ? 'Expand' : 'Collapse'"
      >
        <PanelLeftClose v-if="!collapsed" :size="15" />
        <PanelLeftOpen v-else :size="15" />
        <span v-if="!collapsed">Collapse</span>
      </button>
    </div>
  </aside>
</template>

<script setup>
import { computed } from 'vue';
import { LogOut, PanelLeftClose, PanelLeftOpen, Globe } from 'lucide-vue-next';

const props = defineProps({
  items:     { type: Array, required: true }, // [{ category, links: [{ label, icon, to, count? }] }]
  collapsed: { type: Boolean, default: false },
  user:      { type: Object, default: null },
});
defineEmits(['toggle', 'logout']);

const userInitials = computed(() => {
  const u = props.user;
  if (!u) return '··';
  const f = (u.firstName || '?')[0] || '?';
  const l = (u.lastName || '')[0] || '';
  return (f + l).toUpperCase() || '··';
});

const displayName = computed(() => {
  const u = props.user;
  if (!u) return '';
  return `${u.firstName || ''} ${u.lastName || ''}`.trim();
});
</script>
